import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  MagnifyingGlass,
  Receipt,
  Student as StudentIcon,
  ChalkboardSimple,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import FeeCollectionDialog from "@/components/FeeCollectionDialog";

export default function TeacherCollect() {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [filterClass, setFilterClass] = useState("__all__");
  const [search, setSearch] = useState("");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [collectingFor, setCollectingFor] = useState(null);

  const loadClasses = useCallback(async () => {
    try {
      // Phase 27A — role-aware data source. Teacher endpoint returns ONLY
      // assigned classes (intended scope). Cashier/school_admin get all
      // classes since their operational scope is school-wide.
      const url = user?.role === "teacher"
        ? "/school/teacher/me/classes"
        : "/school/classes";
      const { data } = await api.get(url);
      // /classes can return a wrapped {items: [...]} shape; teacher endpoint
      // returns a plain list. Normalise.
      setClasses(Array.isArray(data) ? data : (data.items || []));
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [user?.role]);

  const loadStudents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterClass !== "__all__") params.set("class_id", filterClass);
      if (search) params.set("search", search);
      params.set("is_active", "true");
      const { data } = await api.get(`/school/students?${params.toString()}`);
      setStudents(data.items);
    } catch (e) {
      setError(formatApiError(e));
    }
    setLoading(false);
  }, [filterClass, search]);

  useEffect(() => { loadClasses(); }, [loadClasses]);
  useEffect(() => {
    const t = setTimeout(loadStudents, 250);
    return () => clearTimeout(t);
  }, [loadStudents]);

  const totalStudents = useMemo(() => classes.reduce((sum, c) => sum + (c.student_count || 0), 0), [classes]);

  return (
    <div className="space-y-6" data-testid="teacher-collect-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Workspace</div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1">
          Collect fees
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Find a student in your assigned classes, view their pending fees, and record a payment.
        </p>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{error}</div>
      )}

      {classes.length === 0 ? (
        <div className="p-12 border border-dashed border-slate-200 bg-white rounded-lg text-center" data-testid="teacher-collect-no-classes">
          <ChalkboardSimple size={32} className="mx-auto text-slate-300 mb-2" />
          <div className="text-sm text-slate-500">
            You haven't been assigned to any classes yet. Ask your school admin to add you.
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 border border-slate-200 bg-white rounded-lg">
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">My classes</div>
              <div className="text-2xl font-bold text-slate-900 font-heading mt-1">{classes.length}</div>
            </div>
            <div className="p-4 border border-slate-200 bg-white rounded-lg">
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Students under my care</div>
              <div className="text-2xl font-bold text-slate-900 font-heading mt-1">{totalStudents}</div>
            </div>
            <div className="p-4 border border-slate-200 bg-white rounded-lg">
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Logged in as</div>
              <div className="text-sm font-medium text-slate-900 mt-1">{user?.name}</div>
              <div className="text-xs text-slate-500">{user?.email}</div>
            </div>
          </div>

          <div className="border border-slate-200 bg-white rounded-lg">
            <div className="p-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 max-w-sm min-w-[200px]">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search by name, admission no…"
                  className="pl-9 h-11 md:h-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="teacher-students-search"
                />
              </div>
              <Select value={filterClass} onValueChange={setFilterClass}>
                <SelectTrigger className="w-full md:w-52 h-11 md:h-9" data-testid="teacher-class-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All my classes</SelectItem>
                  {classes.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}{c.section ? ` - ${c.section}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="overflow-x-auto hidden md:block">
              <table className="w-full text-sm" data-testid="teacher-students-table">
                <thead className="bg-slate-50">
                  <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="text-left font-semibold px-4 py-3">Student</th>
                    <th className="text-left font-semibold px-4 py-3">Adm. no.</th>
                    <th className="text-left font-semibold px-4 py-3">Parent</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading && students.length === 0 ? (
                    <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
                  ) : students.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-12 py-12 text-center" data-testid="teacher-students-empty">
                        <StudentIcon size={32} className="mx-auto text-slate-300 mb-2" />
                        <div className="text-sm text-slate-500">No students match your filters.</div>
                      </td>
                    </tr>
                  ) : (
                    students.map((s) => (
                      <tr key={s.id} className="hover:bg-slate-50 transition-colors" data-testid={`teacher-student-row-${s.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-700">
                              {(s.name || "?").charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <div className="font-medium text-slate-900 truncate">{s.name}</div>
                              <div className="text-xs text-slate-500">{s.class_name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-700">{s.admission_no}</td>
                        <td className="px-4 py-3 text-slate-700">
                          <div className="text-sm">{s.parent_name || "—"}</div>
                          <div className="text-xs text-slate-500 font-mono">{s.parent_phone || ""}</div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            onClick={() => setCollectingFor(s.id)}
                            className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                            data-testid={`collect-fees-${s.id}`}
                          >
                            <Receipt size={14} /> Collect
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile cards — stacked, thumb-friendly */}
            <div className="md:hidden divide-y divide-slate-100" data-testid="teacher-students-cards">
              {loading && students.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-slate-500">Loading…</div>
              ) : students.length === 0 ? (
                <div className="px-6 py-12 text-center" data-testid="teacher-students-empty-mobile">
                  <StudentIcon size={32} className="mx-auto text-slate-300 mb-2" />
                  <div className="text-sm text-slate-500">No students match your filters.</div>
                </div>
              ) : (
                students.map((s) => (
                  <div key={s.id} className="p-4 flex items-center gap-3" data-testid={`teacher-student-card-${s.id}`}>
                    <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-sm font-semibold text-slate-700 shrink-0">
                      {(s.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900 truncate">{s.name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {s.class_name} · <span className="font-mono">{s.admission_no}</span>
                      </div>
                      {s.parent_phone && (
                        <div className="text-xs text-slate-500 font-mono truncate mt-0.5">{s.parent_phone}</div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setCollectingFor(s.id)}
                      className="bg-blue-900 hover:bg-blue-800 gap-1.5 shrink-0 h-11 md:h-9 px-4 md:px-3"
                      data-testid={`collect-fees-mobile-${s.id}`}
                    >
                      <Receipt size={14} /> Collect
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}

      <FeeCollectionDialog
        open={!!collectingFor}
        onOpenChange={(v) => { if (!v) setCollectingFor(null); }}
        studentId={collectingFor}
      />
    </div>
  );
}
